import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { therapists } from "./therapists";
import Env from '../../runtimeEnv';

const CreateMeetingForm = () => {
  const { register, handleSubmit, setValue, watch, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([""]);

  // List of therapists (can be fetched from an API)

  

  // Auto-fill therapist email when a therapist is selected
  const handleTherapistChange = (event) => {
    const therapistEmail = therapists.find((t) => t.name === event.target.value)?.email || "";
    setValue("therapistEmail", therapistEmail);
  };

  const addClient = () => setClients((prev) => [...prev, ""]);
  const removeClient = (index) =>
    setClients((prev) => prev.filter((_, i) => i !== index));
  const updateClient = (index, value) =>
    setClients((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

  const onSubmit = async (formData) => {
  setLoading(true);
  setMessage(null);

  try {
    // sanitize and normalize emails
    let validEmails = clients
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);

    // no duplicates
    const duplicates = validEmails.filter(
      (e, i) => validEmails.indexOf(e) !== i
    );
    if (duplicates.length > 0) {
      setMessage({
        type: "error",
        text: `Duplicate client email(s): ${duplicates.join(", ")}`,
      });
      return;
    }

    const therapistEmail =
      formData.therapistEmail?.trim().toLowerCase() || null;

    if (therapistEmail && validEmails.includes(therapistEmail)) {
      setMessage({
        type: "error",
        text: `Therapist email cannot also be a client.`,
      });
      return;
    }

    if (validEmails.length === 0) {
      setMessage({
        type: "error",
        text: "Please provide at least one client email.",
      });
      return;
    }

    if (!formData.meetingName) formData.meetingName = "Therapy Session";

    const payload =
      validEmails.length === 1
        ? {
            email: validEmails[0],
            therapistEmail,
            sessionTime: formData.sessionTime,
            meetingName: formData.meetingName,
          }
        : {
            emails: validEmails,
            therapistEmail,
            sessionTime: formData.sessionTime,
            meetingName: formData.meetingName,
          };


    const response = await axios.post(
      `${Env.REACT_APP_PYTHON_API_URL}/api/admin/schedule`,
      payload,
      { withCredentials: true }
    );

    setMessage({
      type: "success",
      text: "Session scheduled successfully.",
    });

    reset();
    setClients([""]);
  } catch (error) {
    console.error("❌ Error scheduling session:", error.response?.data || error.message);
    setMessage({
      type: "error",
      text: error.response?.data?.message || "Failed to schedule session.",
    });
  } finally {
    setLoading(false);
  }
};



  return (
    <>
      {message && (
        <div
          className={`p-2 text-center ${
            message.type === "success"
              ? "text-green-600"
              : "text-red-600"
          }`}
        >
          {message.text}
        </div>
      )}

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {/* Multiple Client Emails */}
        <div className="space-y-2">
          <label className="block font-medium">
            Client Emails
          </label>
          {clients.map((email, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) =>
                  updateClient(index, e.target.value)
                }
                placeholder="Client Email"
                className="input-field flex-grow"
                required={index === 0}
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeClient(index)}
                  className="px-2 rounded bg-red-500 text-white"
                >
                  🗑️
                </button>
              )}
              {index === clients.length - 1 && (
                <button
                  type="button"
                  onClick={addClient}
                  className="px-2 rounded bg-blue-500 text-white"
                >
                  +
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Meeting Name */}
        <input
          {...register("meetingName")}
          placeholder="Meeting Name"
          required
          className="input-field"
        />

        {/* Session Time */}
        <input
          type="datetime-local"
          {...register("sessionTime")}
          required
          className="input-field"
        />

        {/* Therapist Selection */}
        <select
          {...register("therapist")}
          required
          className="input-field"
          onChange={handleTherapistChange}
        >
          <option value="">Select a Therapist</option>
          {therapists.map((therapist, index) => (
            <option key={index} value={therapist.name}>
              {therapist.name}
            </option>
          ))}
        </select>

        {/* Auto-filled Therapist Email */}
        <input
          {...register("therapistEmail")}
          placeholder="Therapist Email"
          readOnly
          className="input-field bg-gray-200 cursor-not-allowed"
        />

        {/* Submit Button */}
        <button
          type="submit"
          className="button-primary"
          disabled={loading}
        >
          {loading
            ? "Scheduling..."
            : "Schedule Session"}
        </button>
      </form>
    </>
  );
};

export default CreateMeetingForm;
